import $ from 'jquery'
import _ from 'lodash'
import constants from 'scoreboard/components/constants'
import Backbone from 'backbone'
import url from 'scoreboard/components/url'
import deviceType from 'scoreboard/components/device-type'
import globals from 'scoreboard/components/globals'
import SizeSpy from 'scoreboard/components/size-spy'
import { isV5, isV6 } from 'scoreboard/components/version'
import Mobile from './mobile'
import AppDisplay from './app-display'

class LayoutManager {

	constructor(launchUri) {
		var params = url.getQueryParams(launchUri)

		Object.defineProperties(this, {
			/**
			 * @memberOf LayoutManager#
			 *
			 * Briefing book resources can have different orientations, the scoreboard server
			 * passes the orientation parameter to the export server and we get it back here for
			 * each slide URL.  export-preparer.js will examine the content and the orientation
			 * and then emit so the export server can resize the browser before capturing
			 * the content
			 */
			orientation: {
				value: params.orientation || constants.ORIENTATION_PORTRAIT,
				writable: true
			},
			/**
			 * True if the view is for an export
			 * @type Boolean
			 * @memberOf LayoutManager#
			 *
			 * when an 'export' query param is set to true all user interface chrome
			 * related to changing the screen (head pane, nav pane, display pane tabs)
			 * are hidden
			 */
			export: {
				value: !!params.export,
				writable: true
			},

			/**
			 * Should be one of 'pdf', 'pptx', 'docx', 'png' and is used to set the css
			 * class on the top level DOM element so that correct styling is applied.
			 * @type String
			 * @memberOf LayoutManager#
			 */
			exportType: {
				value: params.exportType,
				writable: true
			},

			/**
			 * True if the view is a shared dashboard. Consider using
			 * sharedResource instead, as dashboards can be embedded in shared
			 * forms, and you will often (but not always) want to treat dashboards
			 * in shared forms the same as shared dashboards.
			 *
			 * @type Boolean
			 * @memberOf LayoutManager#
			 */
			sharedDashboard: {
				value: /^(dashboards|strategy-maps)\/.*\/shared.*/.test(launchUri),
				writable: true
			},

			/**
			 * True if the view is a shared form
			 *
			 * @type Boolean
			 * @memberOf LayoutManager#
			 */
			sharedForm: {
				value: /^(forms)\/.*\/shared.*/.test(launchUri),
				writable: true
			},

			/**
			 * True if the view is a shared resource (eg shared dashboard or
			 * shared form)
			 *
			 * @type Boolean
			 * @memberOf LayoutManager#
			 */
			sharedResource: {
				get () {
					return this.sharedDashboard || this.sharedForm
				}
			}
		})
	}

	setup() {
		this.body = $('body')
		this.contentPanes = $('.slide-pane, .display-pane')
		this.displayPane = $('.display-pane')
		this.navPane = $('.nav-pane')
		this.orgPane = $('.org-pane')
		this.headPane = $('.head-pane')
		this.menuWrapper = $('.menu-wrapper')
		if (isV5()) {
			this.projectOrganizationMenu = $('.project-organization-menu')
		}
		this.rootNode = $('html')
		this.navDisplayPane = $('.nav-display-pane')
		if (isV5()) {
			this.headPaneWidth = this.headPane.width()
		}
		if (isV6()) {
			// The layout was made simpler in V6 using grid to keep everything
			// in sync. But on load, the grid doesn't return the correct width
			// for the headPane, so we'll wait tot set it until the/ user goes
			// to collapse it.
			this.headPane.find('.toggle-collapsed').one('mouseenter', () => {
				this.headPaneWidth = this.headPane.width()
				this.headPane.css('width', this.headPaneWidth)
			})
		}
		this.loggedInAsBanner = $('.logged-in-as-banner')
		this.sizeSpy = new SizeSpy({
			el: this.displayPane,
			classes: constants.DISPLAY_SIZES,
			applyTo: this.body
		})
	}

	/**
	 * Based on the Uri when the page was loaded, set the correct layout.
	 * Intended to be used for static views that the user can not interact with.
	 * (exports, shared dashboards)
	 *
	 * @return {Promise} fulfilled after layout is changed
	 */
	changeLayoutForUri() {
		if (this.export) {
			return layoutManager.changeLayout(constants.LAYOUT_EXPORT)
		} else if (this.sharedDashboard) {
			return layoutManager.changeLayout(constants.LAYOUT_DASHBOARD_SHARED)
		} else if (this.sharedForm) {
			return layoutManager.changeLayout(constants.LAYOUT_FORM_SHARED)
		}
		return Promise.resolve()
	}

	/**
	 * Toggles whether the organization toggler in the upper left corner of the
	 * screen is visible
	 * @param visible
	 */
	toggleProjectOrganizationMenu(visible) {
		if (visible) {
			// For some reason, on newer versions of safari, the .project-organization-menu
			// fails to render correctly the first time around. Delaying slightly
			// forces a repaint to ensure full rendering (#14079)
			if (deviceType.touch && deviceType.safari) {
				process.nextTick(function() {
					this.body.addClass('has-selected-org')
				}.bind(this))
			} else {
				this.body.addClass('has-selected-org')
			}
		} else {
			this.body.removeClass('has-selected-org')
		}
	}

	/**
	 *
	 * @param {boolean} [collapse] If true, the head-pane will collapse to
	 * narrow state without labels. If false, it will be at its full width.
	 * @param {boolean} [hide] Hides the head-pane entirely or shows it.
	 * @returns
	 */
	toggleHeadCollapsed(collapse, hide) {
		if (collapse == undefined) {
			collapse = !this.isHeadCollapsed()
		}

		return new Promise((resolve) => {
			// Is it already collapsed/expanded?
			if (_.isUndefined(hide) && ((collapse && this.isHeadCollapsed()) ||
				(!collapse && !this.isHeadCollapsed()) ||
				Mobile.isMobileOrientation())) {
				resolve()
				return
			}

			// If the head-pane is being hidden or shown because the user
			// switched between library and non library sections.
			if (!_.isUndefined(hide)) {
				this.headPane.toggleClass('is-not-displayed', hide)
				if (!hide) {
					this.headPane.css('width', '')
				}
				return
			}
			var headPaneWidth = collapse ?
				constants.HEAD_PANE_COLLAPSED_WIDTH : this.headPaneWidth
			this.headPane.width(headPaneWidth)

			// Apply/Remove classes so the project-organization-menu can transition
			// and show/hide elements as needed
			this.headPane.toggleClass('is-collapsed', collapse)
			this.headPane.trigger('toggleCollapse')

			this.navDisplayPane.toggleClass('is-head-collapsed', collapse)
			if (isV5())	{
				this.navDisplayPane.css('margin-inline-start', headPaneWidth)
				this.projectOrganizationMenu
					.addClass('head-pane-show-hide') // match headPane transition speed
					.toggleClass('is-collapsed-medium', collapse)
			}
			this.orgPane.toggleClass('is-head-collapsed', collapse)
			this.body.addClass('is-head-pane-collapsing')

			if (isV5()) {
				var orgTogglerWidth = headPaneWidth + (!this.isNavCollapsed() ? this.navPane.width() : 0)
				this.projectOrganizationMenu.css('width', orgTogglerWidth + 'px')
			}

			this.adjustDisplayHeader()

			function finish() {
				this.body.removeClass('is-head-pane-collapsing')
				if (isV5()) {
					this.projectOrganizationMenu.removeClass('head-pane-show-hide')
				}
				resolve()
			}

			if (!this.body.hasClass('disable-transitions')) {
				this.headPane.one('transitionend', finish.bind(this))
			} else {
				process.nextTick(finish.bind(this))
			}
			this.trigger('toggle:isHeadCollapsed', collapse)
		})
	}

	/**
	 * Toggles the expand/collapse state of the navigation pane.
	 *
	 * @param {boolean} [collapse] Determines whether the nav-pane
	 * should be collapsed or expanded.
	 * @param {boolean} [autoCollapsed] Indicates whether the navigation
	 * pane was auto-collapsed due to visiting a section (true) or manually
	 * collapsed by the user (false).
	 *
	 * @return {Promise} Resolves when the toggle operation is complete.
	 */
	toggleNavCollapsed(collapse, autoCollapsed) {
		if (collapse == undefined) {
			collapse = !this.isNavCollapsed()
		}

		// Keep track of wether the nav pane was auto collapsed because of
		// visiting a section and not because the user manually collapsed it.
		this.navAutoCollapsed = autoCollapsed

		return new Promise((resolve) => {
			// Is it already collapsed/expanded?
			if ((collapse && this.isNavCollapsed()) || (!collapse && !this.isNavCollapsed()) || Mobile.isMobileOrientation()) {
				resolve()
				return
			}

			function finish() {
				if (isV5()) {
					this.projectOrganizationMenu.removeClass('nav-pane-show-hide')
				}
				this.displayPane.removeClass('is-nav-collapsing')
				this.navPane.off('transitionend')
				this.trigger('toggle:isNavCollapsed', collapse)
				resolve()
			}

			if (!this.body.hasClass('disable-transitions')) {
				this.navPane.one('transitionend', finish.bind(this))
			} else {
				process.nextTick(finish.bind(this))
			}
			if (isV5()) {
				// Apply class used to match transition speed of the nav-pane
				this.projectOrganizationMenu.addClass('nav-pane-show-hide')
			}

			// Apply/Remove classes so the project-organization-menu can transition
			// and show/hide elements as needed
			var navWidth = this.navPane.width()
			var width = (this.isNavCollapsed() ? navWidth : 0) + this.headPane.width()
			this.headPane.toggleClass('is-nav-collapsed', collapse)
			this.displayPane.addClass('is-nav-collapsing')
			this.contentPanes.css('inset-inline-start', !collapse ? navWidth : 0)
			this.navPane.toggleClass('is-collapsed', collapse)
			this.navPane.css('inset-inline-start', !collapse ? 0 : navWidth * -1)
			if (isV6()) {
				let columns = collapse ? '0px 1fr' : ''
				this.navDisplayPane.css({'grid-template-columns': columns })
			}
			if (isV5()) {
				this.projectOrganizationMenu
					.toggleClass('is-collapsed-small', collapse)
					.css('width', width)
			}
			this.adjustDisplayHeader()
		})
	}

	toggleNavCollapsible(collapsible) {
		this.headPane.toggleClass('is-not-collapsible', !collapsible)
		this.navPane.toggleClass('is-not-collapsible', !collapsible)
	}

	// Toggles the layout of the app.
	changeLayout(newLayout) {
		let p = new Promise((resolve) => {
			// If we're already in the middle of changing our layout, ignore subsequent
			// calls as to not mix things up and get out of sync
			if (this.isChangingLayout) {
				return
			}

			this.isChangingLayout = true

			switch (newLayout) {
				case constants.LAYOUT_EXPORT:
				case constants.LAYOUT_DASHBOARD_SHARED:
					// We're entering LAYOUT_EXPORT OR LAYOUT_DASHBOARD_SHARED which is used in our export screens.
					// Nothing much to do here js-wise, .fullscreen-mode takes care of it all.
					this.changeLayoutClasses(newLayout)
					this.isChangingLayout = false
					resolve()
					break
				case constants.LAYOUT_FORM_SHARED:
					this.changeLayoutClasses(constants.LAYOUT_FORM_SHARED)
					this.isChangingLayout = false
					resolve
					break
				// TODO: Remove when V5 is no longer supported (dashboard edit fullscreen stuff)
				case constants.LAYOUT_DASHBOARD_EDIT_FULLSCREEN:
					// We're entering LAYOUT_DASHBOARD_EDIT_FULLSCREEN which is used in the dashboards edit screen.
					// Nothing much to do here js-wise, .layout-dashboard-edit-fullscreen takes care of it all.
					this.changeLayoutClasses(newLayout)
					this.isChangingLayout = false
					resolve()
					break
				case constants.LAYOUT_FULLSCREEN:
						// We're entering LAYOUT_FULLSCREEN which changes the
						// layout so the display is fullscreen and side and top
	 					// navigation is hidden.
						this.changeLayoutClasses(newLayout)
						this.isChangingLayout = false
						resolve()
						break
				case constants.LAYOUT_STANDARD:
					// We're entering the standard app layout.
					// Make the .nav-pane and .head-pane slide out unless we're in LAYOUT_EXPORT, LAYOUT_DASHBOARD_SHARED or
					// LAYOUT_DASHBOARD_EDIT_FULLSCREEN, in which case, there's no need to adjust those panes
					if (_.includes([constants.LAYOUT_EXPORT,
						constants.LAYOUT_DASHBOARD_SHARED,
						constants.LAYOUT_DASHBOARD_EDIT_FULLSCREEN,
						constants.LAYOUT_FULLSCREEN],
						this.getLayout())) {
						this.changeLayoutClasses()
						this.isChangingLayout = false
						resolve()
					} else {
						this.toggleNavCollapsed(false).then(() => {
							this.changeLayoutClasses()
							this.isChangingLayout = false
							resolve()
						})
					}
					break
			}
		})

		// #16442 always need to resize app-display after changing layout
		// Need to do require() here to avoid circular dependency
		p.then(() => AppDisplay.resize())

		return p
	}

	/**
	 * @return {boolean} true if the current layout requires the org nav to be visible
	 */
	hasOrgNav() {
		return globals.isOperationalMode() && !this.inLayout([constants.LAYOUT_EXPORT, constants.LAYOUT_DASHBOARD_SHARED])
	}

	/**
	 * @param {String|String[]} layouts use constants.LAYOUT_*
	 * @return {Boolean} true if the layout is currently one of the provided layouts
	 */
	inLayout() {
		return _.some(_.flatten(arguments), layout => this.body.hasClass(layout))
	}

	getLayout() {
		if (this.body.hasClass(constants.LAYOUT_EXPORT)) {
			return constants.LAYOUT_EXPORT
		}

		if (this.body.hasClass(constants.LAYOUT_DASHBOARD_SHARED)) {
			return constants.LAYOUT_DASHBOARD_SHARED
		}

		if (isV5()) {
			if (this.body.hasClass(constants.LAYOUT_DASHBOARD_EDIT_FULLSCREEN)) {
				return constants.LAYOUT_DASHBOARD_EDIT_FULLSCREEN
			}
		}

		if (this.body.hasClass(constants.LAYOUT_FULLSCREEN)) {
			return constants.LAYOUT_FULLSCREEN
		}
	}

	getDisplaySize() {
		return _.intersection(this.body[0].classList, constants.DISPLAY_SIZES)[0]
	}

	/**
	 * MOBILE LAYOUT MANAGEMENT
	 */

	// Toggles the mobile side menu visibiliy.
	toggleMobileMenu(close) {
		return new Promise((resolve) => {
			if (close) {
				// If the menu is closing, remove the dismiss overlay, then shift the
				// content panes, display header, and sticky footer (if there is one)
				// back to the left and hide the navPane
				this.rootNode.toggleClass('is-mobile-menu-visible', false)
				this.contentPanes.css('inset-inline-start', 0)
				this.contentPanes.css('inset-inline-end', 0)
				this.displayPane.find('.display-pane-header, .sticky-footer').css('inset-inline-start', 0)
				this.navPane.css('inset-inline-start', -1 * this.navPane.width())
				this.menuWrapper.css('inset-inline-start', -1 * this.menuWrapper.width())
				this.contentPanes.one('transitionend', resolve)
			} else {
				// If the menu is opening shift the content panes, display header, & sticky
				// footer (if there is one) over to make room for the navPane which
				// we'll extend here too
				this.rootNode.toggleClass('is-mobile-menu-visible', true)
				this.contentPanes.css('inset-inline-start', this.navPane.width())
				this.contentPanes.css('inset-inline-end', -1 * this.navPane.width())
				this.displayPane.find('.display-pane-header').css('inset-inline-start', 0)
				this.displayPane.find('.sticky-footer').css('inset-inline-start', $('.nav-pane').width())
				this.navPane.css('inset-inline-start', 0)
				this.menuWrapper.css('inset-inline-start', 0)
			}
		})
	}

	// For the notes layout, the display pane header assumes a fixed position with
	// its right positioning at 0 and its width auto. This means we'll need to set
	// the left value relative to the body.
	adjustDisplayHeader() {
		if (this.isNotesLayout()) {
			var headPaneWidth = this.isHeadCollapsed() ? constants.HEAD_PANE_COLLAPSED_WIDTH : this.headPane.width()
			var navPaneWidth = this.isNavCollapsed() ? 0 : this.navPane.width()
			var displayPaneWidth = this.body.width() - (headPaneWidth + navPaneWidth)
			this.displayPane.find('.display-pane-header').css('inset-inline-start', this.body.width() - displayPaneWidth)
		}
	}

	/**
	 * HELPER METHODS
	 */

	// Tells whether the .head-pane is collapsed or not
	isHeadCollapsed() {
		return this.headPane.hasClass('is-collapsed') || parseInt(this.headPane.css('inset-inline-start')) < 0
	}

	// Tells whether the .nav-pane is collapsed or not
	isNavCollapsed() {
		return this.navPane.hasClass('is-collapsed')
	}

	// Tells whether the .display-pane is in the notes layout (contains notes at the bottom eg. Scoreards Overview)
	isNotesLayout() {
		return $('.display-pane .notes-layout').length > 0 ||
			this.displayPane.find('.display-pane-header').css('position') == 'fixed' ||
			this.displayPane.find('.display-pane-header').css('position') == 'static'
	}

	// Returns the margin at the top of the entire app which can be created by
	// the introduction of a banner (ex. 'logged in as user' impersonation banner)
	getAppTopMargin() {
		if (this.loggedInAsBanner.hasClass('is-selected')) {
			return constants.LOGGED_IN_AS_BANNER_HEIGHT
		}

		return 0
	}

	// Append the correct class to the body in order to switch between layouts (layouts.scss)
	changeLayoutClasses(layout) {
		this.body
			.removeClass(`${constants.LAYOUT_EXPORT} ` +
						 `${constants.LAYOUT_FULLSCREEN} ` +
						 `${constants.LAYOUT_DASHBOARD_SHARED} ` +
						 `${constants.LAYOUT_FORM_SHARED} ` +
 						 // TODO: Remove when V5 is no longer supported (dashboard edit fullscreen)
						 `${constants.LAYOUT_DASHBOARD_EDIT_FULLSCREEN} ` +
						 `prep-animate-presentation`)
			.addClass(layout)
	}

	// We need to be able to adjust a few pieces of the layout when we resize
	// the window. This is called in main.js where needed (resizing of window,
	// collapsing/expanding of panes, etc.)
	resize() {
		this.sizeSpy.spy()

		// Emit a 'resize' trigger so that views can listen for it instead of
		// independently listening to the window resize event. Listening for
		// this event here (unlike listening for the window resize event) ensures
		// that the sizeSpy classes are correct for the current window size.
		this.trigger('resize')
	}

	// Regulates app transitions by toggling .disable-transitions on the body.
	// When passed true, transitions are enabled.
	// When passed false, transitions are disabled.
	toggleTransitions(toggle) {
		this.body.toggleClass('disable-transitions', !toggle)
	}

	transitionsEnabled() {
		return !this.body.hasClass('disable-transitions')
	}

	getMenuWidth() {
		let headWidth = this.headPane.width()
		if (this.isNavCollapsed()) {
			return headWidth
		}
		return headWidth + this.navPane.width()
	}
}

_.extend(LayoutManager.prototype, Backbone.Events)

var layoutManager
function newLayoutManager() {
	layoutManager = new LayoutManager(window.requestedUri)
}
newLayoutManager()

_.extend(layoutManager, {
	LayoutManager,
	newLayoutManager
})
export default layoutManager
export { LayoutManager }
export { newLayoutManager }

layoutManager.__filename__ = 'components/layout-manager.js'
